import React, {useContext, useEffect} from "react"
import capitalize from "lodash/capitalize"
import {
    ChipsContainer,
    ImageChip,
    EmblaButtonArrow,
    EmblaButton,
    StyledImage,
    StyledLink,
    StyledTypography,
    EmblaCarousel,
    EmblaCarouselContainer,
} from "./components"
import {useCarouselActions} from "../../../hooks/useCarouselActions"
import {useReadFeatureSetting} from "../../../contexts/useReadFeatureSetting"
import {ImageChipsProps, ImageStyles, ImageChip as ImageChipComponent} from "./models"
import {
    SearchBannerContext,
    getHeightAndWidth,
    replaceSiteUrl,
    useUniqueKey,
    getSearchBannerComponentTestId,
} from "../utils"
import {PREVIOUS, NEXT} from "../../../config/constants"
import TrackVisualPillsImpression from "../../../events/trackEvent/events/trackVisualPillsImpression"
import TrackVisualPillsClick from "../../../events/trackEvent/events/trackVisualPillsClick"
import env from "../../../config/env"
import {FeatureSettingKeys} from "../../../models/FeatureFlags"

const handleVisualPillsImpression = imageChips => {
    if (imageChips?.length > 0) {
        const imageChipsImpression = imageChips.reduce((agg, chip) => {
            return agg ? `${agg} | ${chip.accessibilityText || ""}` : chip.accessibilityText
        }, "")
        TrackVisualPillsImpression(imageChipsImpression)
    }
}

/**
 * Helper function to render image chips
 */
function renderImageChips(
    components: ImageChipComponent[],
    chipKeys: string[],
    containerStyles: Record<string, any>,
    textStyles: Record<string, any>,
    imageStyles: ImageStyles,
    siteUrl: string,
    enableSearchBannerCarousel: boolean,
): React.ReactNode[] {
    return components?.map((imageChip, idx) => {
        const {url, text, accessibilityText, accessibilityTooltip, image} = imageChip
        return (
            <ImageChip
                key={chipKeys[idx]}
                $containerStyles={containerStyles}
                $isSearchBannerCarousel={enableSearchBannerCarousel}
                data-testid="search-banner-image-chip"
                className={enableSearchBannerCarousel ? "embla__slide" : undefined}
            >
                <StyledLink
                    href={replaceSiteUrl(url, siteUrl)}
                    aria-label={accessibilityText}
                    title={accessibilityTooltip}
                    onClick={() => TrackVisualPillsClick(text || "", replaceSiteUrl(url, siteUrl))}
                >
                    <StyledImage src={image} alt={accessibilityText} $imageStyles={imageStyles} />
                    <StyledTypography $textStyles={textStyles}>{text}</StyledTypography>
                </StyledLink>
            </ImageChip>
        )
    })
}

export const EmblaCarouselActions = ({
    actionType,
    disabled,
    action,
    imageStyles,
}: {
    actionType: string
    disabled: boolean
    action: () => void
    imageStyles: ImageStyles
}) => {
    return (
        <EmblaButton
            className={`embla_button_${actionType}`}
            disabled={disabled}
            onClick={action}
            $imageStyles={imageStyles}
            data-testid={`embla-carousel-${actionType}-button`}
        >
            <EmblaButtonArrow
                className={`embla_button_arrow_${actionType}`}
                src={`${env.REACT_APP_BLOB_STORAGE_PATH}/icons/shared/carousel-arrow.svg`}
                alt={capitalize(actionType)}
            />
        </EmblaButton>
    )
}

export const ImageChips = ({overrideStyles, content, isIntelRecs}: ImageChipsProps) => {
    const {components} = content

    const {emblaRef, prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick} = useCarouselActions()
    const {siteUrl} = useContext(SearchBannerContext)
    const chipKeys = useUniqueKey(components?.length ?? 0)
    const enableSearchBannerCarousel = useReadFeatureSetting(FeatureSettingKeys.enableSearchBannerCarousel) as boolean
    const textStyles = {
        textColor: overrideStyles?.textColor,
        fontWeight: overrideStyles?.fontWeight,
        textTransform: overrideStyles?.textTransform,
        fontSize: overrideStyles?.fontSize,
        lineClamp: overrideStyles?.lineClamp,
    }
    const [width, height] = getHeightAndWidth(overrideStyles?.imageSize)
    const imageStyles = {
        borderColor: overrideStyles?.borderColor,
        borderRadius: overrideStyles?.borderRadius,
        gap: overrideStyles?.gap,
        height,
        width,
    }
    const containerStyles = {
        width,
        gap: overrideStyles?.gap,
    }
    useEffect(() => {
        handleVisualPillsImpression(components)
    }, [])
    return enableSearchBannerCarousel ? (
        <EmblaCarousel className="embla">
            <EmblaCarouselActions
                actionType={PREVIOUS}
                disabled={prevBtnDisabled}
                action={onPrevButtonClick}
                imageStyles={imageStyles}
            />
            <ChipsContainer
                data-testid={getSearchBannerComponentTestId(isIntelRecs, "image-chips")}
                $containerStyles={containerStyles}
                $chipCount={components?.length || 0}
                $isSearchBannerCarousel={enableSearchBannerCarousel}
                className="embla__viewport"
                ref={emblaRef}
            >
                <EmblaCarouselContainer className="embla__container">
                    {renderImageChips(
                        components || [],
                        chipKeys,
                        containerStyles,
                        textStyles,
                        imageStyles,
                        siteUrl,
                        enableSearchBannerCarousel,
                    )}
                </EmblaCarouselContainer>
            </ChipsContainer>
            <EmblaCarouselActions
                actionType={NEXT}
                disabled={nextBtnDisabled}
                action={onNextButtonClick}
                imageStyles={imageStyles}
            />
        </EmblaCarousel>
    ) : (
        <ChipsContainer
            data-testid={getSearchBannerComponentTestId(isIntelRecs, "image-chips")}
            $containerStyles={containerStyles}
            $chipCount={components?.length || 0}
            $isSearchBannerCarousel={enableSearchBannerCarousel}
        >
            {renderImageChips(
                components || [],
                chipKeys,
                containerStyles,
                textStyles,
                imageStyles,
                siteUrl,
                enableSearchBannerCarousel,
            )}
        </ChipsContainer>
    )
}

export default ImageChips
